import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, debounceTime, filter, map, mapTo, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SchemePdf } from './environment-diff.service';
import { PimProductInfo } from './products.service';
import { BomItem } from '../components/questionnaire/configit-quest-adapter/configit-types.interface';
import { LineType, Offer, QuotationQuoteLine, QuoteAssignment, QuoteStatus } from '../models/quote.model';
import {
    ConfigitQuoteLineAdapter,
    HasAssignments,
    HasQuantity,
    QuotationQuoteLineAdapter,
    QuoteLineAdapter,
} from '../adapters/QuoteLineAdapter';
import { PrintType, PrintTypeLong, PrintTypeShort } from '../models/print-type';
import { QuotePermissionService } from './quote-permission.service';

@Injectable({
    providedIn: 'root',
})
export class QuoteService {
    private quote$: BehaviorSubject<Quote | undefined> = new BehaviorSubject(undefined);
    private lineAdapter: QuoteLineAdapter<HasAssignments, HasQuantity>;
    public quoteErrors$: Subject<any> = new Subject<any>();

    constructor(private httpService: HttpClient, private quoteValidation: QuotePermissionService) {
        this.quote$
            .pipe(
                filter(Boolean),
                filter((v: Quote) => !v?.isSaved),
                debounceTime(2500),
                concatMap((quote) =>
                    this.performQuoteUpdateRequest(quote).pipe(
                        map((v) => ({ ...v, isSaved: true })),
                        catchError((error) => {
                            this.quoteErrors$.next(error);
                            const quote = this.quote$.value;
                            const { version } = quote?.isLegacy ? quote?.configitQuote : quote?.quotationQuote;
                            return of({ version, isSaved: true });
                        })
                    )
                )
            )
            .subscribe(({ version, isSaved }) => {
                const data = this.quote$.getValue();
                if (data) {
                    const quote = data.isLegacy
                        ? { configitQuote: { ...data.configitQuote, version } }
                        : { quotationQuote: { ...data.quotationQuote, version } };
                    this.quote$.next({ ...data, ...quote, isSaved });
                }
            });
    }

    public getCurrentQuoteObservable() {
        return this.quote$.pipe(filter((q: Quote, index) => q === undefined || index === 0 || q.isNew || !q.isSaved));
    }

    public getCurrentQuote() {
        return this.quote$.value;
    }

    public fetchQuote(id: string, forceReload = false): Observable<Quote> {
        const quote = this.quote$.getValue();
        const currentId = quote?.isLegacy ? quote?.configitQuote?.documentId : quote?.quotationQuote.id;
        if (!forceReload && currentId === id) {
            this.quote$.next({
                ...quote,
                isNew: true,
            });
            return of(this.quote$.value);
        }
        this.quote$.next(undefined);

        const isNumber = /^[+-]?\d+(\.\d+)?$/.test(id);
        return (isNumber ? this.fetchQuotationQuote(id) : this.fetchQuoteFromConfigit(id)).pipe(
            tap((q) => {
                this.lineAdapter = q.isLegacy ? new ConfigitQuoteLineAdapter() : new QuotationQuoteLineAdapter();
                this.quoteValidation.check(q);
            })
        );
    }

    private fetchQuoteFromConfigit(id: string): Observable<Quote> {
        return this.httpService.get(`${environment.http.baseUrl}configuration/quotations/${id}?revision=0`).pipe(
            map(({ quote }: { quote: ConfigitQuote }) => ({
                configitQuote: quote,
                isLegacy: true,
                isSaved: true,
                isNew: true,
                ...this.getConfigitQuoteMetaProperties(quote),
            })),
            tap((result) => this.quote$.next(result))
        );
    }

    private fetchQuotationQuote(id: string): Observable<Quote> {
        return this.httpService
            .get<QuotationQuote>(`${environment.http.quotation}quotations/quote-assist-pro/quotes/${id}`)
            .pipe(
                map((quotationQuote) => ({
                    quotationQuote,
                    isLegacy: false,
                    isSaved: true,
                    isNew: true,
                    ...this.getQuotationQuoteMetaProperties(quotationQuote),
                })),
                tap((result) => this.quote$.next(result))
            );
    }

    private getConfigitQuoteMetaProperties(quote: ConfigitQuote): QuoteMetaProperties {
        return {
            isLegacy: true,
            salesOrg: quote.salesArea.salesOrganization,
            customerPurchaseNumber: quote.title,
            quickRef: quote.quickRef,
            status: quote.status,
            currency: quote.currency,
            offerNumber: quote.salesDocumentNumber,
            customerNumber: <string>this.extractQuoteProperty(quote, 'companyId'),
            energyLabelHash: this.extractQuoteProperty(quote, 'energyLabelHash'),
            schemePdfs: {
                primary: this.extractQuoteProperty(quote, 'primarySchemes'),
                secondary: this.extractQuoteProperty(quote, 'secondarySchemes'),
            },
            externalApp: this.extractQuoteProperty(quote, 'externalApp'),
            printType: PrintType.toLong(<PrintTypeShort>this.extractQuoteProperty(quote, 'printType')),
            projectId: this.extractQuoteProperty(quote, 'projectIdSF'),
        };
    }

    private getQuotationQuoteMetaProperties({ offer, quickRef, offerNumber }: QuotationQuote): QuoteMetaProperties {
        return {
            isLegacy: false,
            salesOrg: offer.salesOrg,
            customerPurchaseNumber: offer.customerPurchaseNumber,
            quickRef,
            status: offer.extraData.status,
            currency: offer.currency,
            offerNumber: offerNumber || offer.documentNumber,
            customerNumber: offer.customerNumber || '',
            schemePdfs: {
                primary: offer.extraData.primarySchemes,
                secondary: offer.extraData.secondarySchemes,
            },
            printType: offer.printOptions.printType,
            externalApp: offer.extraData.externalApp,
            projectId: offer.submissionNumber,
            energyLabelHash: offer.extraData.energyLabelHash,
        };
    }

    private extractQuoteProperty(quote: ConfigitQuote, propertyKey: string): string | undefined {
        return quote.properties.find((element) => element.key === propertyKey)?.value?.replace(/"/g, '');
    }

    public updateQuoteAssignments(assignments: QuoteAssignment[], material: string) {
        const q = this.getCurrentQuote();
        if (q) {
            const filteredAssignments = assignments.filter(
                (assignment) => assignment.isUserAssignment === true || assignment.isDefault === true
            );
            const line = this.lineAdapter.getLineByMaterial(q, material);
            this.setLineAssignments(filteredAssignments, line);
            this.updateQuote(q);
        }
    }

    public setLineAssignments(assignments: QuoteAssignment[], line: HasAssignments) {
        this.lineAdapter.setLineAssignments(assignments, line);
    }

    public updateQuoteTitle(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateIfChanged(quote.title, value, () => (quote.title = value)),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.customerPurchaseNumber,
                    value,
                    () => (quote.offer.customerPurchaseNumber = value)
                )
        );
    }

    public updateEnergyLabelHash(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('energyLabelHash', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.extraData.energyLabelHash,
                    value,
                    () => (quote.offer.extraData.energyLabelHash = value)
                )
        );
    }

    public updateCustomerNumber(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('companyId', value, quote),
            (quote) =>
                this.updateIfChanged(quote.offer.customerNumber, value, () => (quote.offer.customerNumber = value))
        );
    }

    public updateProjectId(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('projectIdSF', value, quote),
            (quote) =>
                this.updateIfChanged(quote.offer.submissionNumber, value, () => (quote.offer.submissionNumber = value))
        );
    }

    public updatePrintType(value: PrintTypeLong) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('printType', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.printOptions.printType,
                    value,
                    () => (quote.offer.printOptions.printType = value)
                )
        );
    }

    private updatePrimarySchemes(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('primarySchemes', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.extraData.primarySchemes,
                    value,
                    () => (quote.offer.extraData.primarySchemes = value)
                )
        );
    }

    private updateSecondarySchemes(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('secondarySchemes', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.extraData.secondarySchemes,
                    value,
                    () => (quote.offer.extraData.secondarySchemes = value)
                )
        );
    }

    public updateSchemePdfs(schemes: SchemePdf) {
        if (Array.isArray(schemes?.primary)) {
            this.updatePrimarySchemes(schemes.primary.join(';'));
        }
        if (Array.isArray(schemes?.secondary)) {
            this.updateSecondarySchemes(schemes.secondary.join(';'));
        }
    }

    private updateIfChanged<T>(currentValue: T, newValue: T, setter: () => void): boolean {
        if (currentValue !== newValue) {
            setter();
            return true;
        }
        return false;
    }

    private updateQuoteProperty(
        configitUpdateFunc: (quote: ConfigitQuote) => boolean,
        quotationUpdateFunc: (quote: QuotationQuote) => boolean
    ) {
        const data = this.quote$.getValue();
        if (!data) {
            return of(undefined);
        }
        const changed = data.isLegacy
            ? configitUpdateFunc(data.configitQuote)
            : quotationUpdateFunc(data.quotationQuote);
        if (changed) {
            return this.updateQuote(data);
        }
        return of(undefined);
    }

    public updateConfigitQuoteProperty(propertyKey: string, newValue: string, quote: ConfigitQuote): boolean {
        const newSaveValue = `"${newValue}"`;
        let hasChanged = false;
        const updated: QuoteProperty[] = quote.properties.map((property) => {
            if (property.key === propertyKey && property.value !== newSaveValue) {
                hasChanged = true;
                return { ...property, value: newSaveValue };
            }
            return property;
        });
        const added: QuoteProperty[] = !updated.some((p) => p.key === propertyKey)
            ? [
                  {
                      key: propertyKey,
                      value: newSaveValue,
                  },
              ]
            : [];
        quote.properties = [...updated, ...added];
        return hasChanged || !!added.length;
    }

    public createAdditionalLine(
        material: string,
        type: LineType,
        bomItems: (BomItem & { info: PimProductInfo })[],
        assignments: QuoteAssignment[] = []
    ) {
        const quote = this.getCurrentQuote();
        this.lineAdapter.createConfigurationLine(material, type, bomItems, assignments, quote);

        this.updateQuote(quote);
        return this.getQuoteSaveObservable();
    }

    public getPrimaryLine(quote: Quote): QuotationQuoteLine {
        return this.getLineByType(quote, LineType.primary);
    }

    public getSecondaryLine(quote: Quote): QuotationQuoteLine {
        return this.getLineByType(quote, LineType.secondary);
    }

    public getLineByType(quote: Quote, type: LineType): QuotationQuoteLine {
        return this.lineAdapter.getLineByType(quote, type);
    }

    public ensureSubLine(bomItemId: string, quantity: number, materialInfo: PimProductInfo, quote: Quote) {
        if (quote) {
            const changed = this.lineAdapter.ensureProductItem(bomItemId, quantity, materialInfo, quote);
            if (changed) {
                this.updateQuote(quote);
            }
        }
    }

    public pruneProductItems(materials: string[]) {
        const quote = this.getCurrentQuote();
        const changed = this.lineAdapter.pruneProductItems(materials, quote);
        if (changed) {
            this.updateQuote(quote);
        }
    }

    private updateQuote(quote: Quote) {
        const data = this.quote$.getValue();
        if (data) {
            const { configitQuote, quotationQuote } = quote;

            this.quote$.next({
                ...data,
                isSaved: false,
                isNew: false,
                ...(quote.isLegacy
                    ? { configitQuote, ...this.getConfigitQuoteMetaProperties(configitQuote) }
                    : { quotationQuote, ...this.getQuotationQuoteMetaProperties(quotationQuote) }),
            });
            return this.getQuoteSaveObservable();
        }
        return of(undefined);
    }

    public getQuoteSaveObservable() {
        return this.quote$.pipe(
            filter((q) => q?.isSaved),
            take(1),
            mapTo(undefined)
        );
    }

    public reset() {
        this.quote$.next(undefined);
    }

    private performQuoteUpdateRequest(quote: Quote): Observable<{ version: string }> {
        return quote.isLegacy
            ? this.httpService.put<{ version: string }>(
                  `${environment.http.baseUrl}configuration/quotations/${quote.configitQuote.documentId}?revision=0`,
                  quote.configitQuote
              )
            : this.httpService.put<{ version: string }>(
                  `${environment.http.quotation}quotations/quote-assist-pro/quotes/${quote.quotationQuote.id}`,
                  quote.quotationQuote
              );
    }

    public convertQuote(conversion: QuoteConversion) {
        const url = `${environment.http.quotation}quotations/quote-assist-pro/quotes/${conversion.id}`;
        const { id, ...conversionBody } = conversion;
        return this.httpService.patch(url, conversionBody);
    }
}

export interface QuoteMetaProperties {
    isLegacy: boolean;
    salesOrg: string;
    customerPurchaseNumber: string;
    quickRef: string;
    status: QuoteStatus;
    offerNumber: string;
    currency: string;
    customerNumber: string;
    externalApp?: string;
    printType?: PrintTypeLong;
    projectId?: string;
    schemePdfs: { primary: string; secondary: string };
    energyLabelHash: string;
}

export interface Quote extends QuoteMetaProperties {
    quotationQuote?: QuotationQuote;
    configitQuote?: ConfigitQuote;
    isSaved: boolean;
    isNew: boolean;
}

interface QuoteProperty {
    key: string;
    value: string;
}

export interface QuotationQuote {
    id: number;
    version: string;
    quickRef: string;
    offerNumber: string;
    offer: Offer;
}

export interface ConfigitQuote {
    language: string;
    documentId: string;
    quickRef: string;
    revisionId: string;
    revision: string;
    title: string;
    status: QuoteStatus;
    salesDocumentNumber: string;
    lines: QuoteLine[];
    properties: QuoteProperty[];
    version: string;
    currency: string;
    salesArea: {
        salesOrganization: string;
    };
    validTo?: string;
}

export interface QuoteLine {
    id: string;
    lineId: string;
    parentId?: string;
    bomItemId: string;
    isSubBomItem?: boolean;
    variantCode: string;
    quantity: number;
    description: {
        serializedTranslations: SerializedTranslation[];
    };
    properties: QuoteProperty[];
    assignments: QuoteAssignment[];
    language: string;
    materialVariant: {
        language: string;
        code: string;
        image: string;
        description: {
            serializedTranslations: SerializedTranslation[];
        };
        assignments: [];
        material: MaterialInfo;
    };
    lineType: number;
    createdDate: string;
    presetSet: boolean;
    modelLineType: number;
    valid: boolean;
}

export interface SerializedTranslation {
    language: string;
    translation: string;
    isDefault: boolean;
}

export interface MaterialInfo {
    language: string;
    properties: QuoteProperty[];
    name: string;
    externalId: string;
    description: {
        serializedTranslations: SerializedTranslation[];
    };
}

export enum LineProperty {
    optional = 'OptionalOrAlternative',
}

export interface QuoteConversion {
    id: string;
    type?: string;
    templateDescription?: string;
    primarySchemes?: string;
    customerPurchaseNumber?: string;
}
